


import { Component, Vue } from "vue-property-decorator";

import { Coupons, Records, Filters } from "App";
import { Coupon, CustomError, PagerSorter, Column } from "App/Models";
import { Filter } from "App/Contracts";
import { CustomValue } from "@Internal/Models";
import { EventBus, $t } from "App/IOC";

import { TableAction } from "Web/Contracts";

import RecordsComponent from "Web/Components/Records.vue";
import CouponForm from "./Form.vue";

@Component({
    components: {
        CouponForm,
        Records: RecordsComponent
    }
})
export default class CouponsView extends Vue {
    $refs!: Vue[ "$refs" ] & {
        Records: RecordsComponent,
    };

    private columns: Column[] = [];
    private records: Coupon[] = [];

    private filters = Coupons.Filters.Get;

    /* Gates the Records render until the default "Active" status filter is assigned, so the
       MultiFilter component doesn't mount before it and apply an empty default. Initialized to a
       non-undefined placeholder so it stays reactive; reset to undefined in created(). */
    private id?: string = {} as string;

    private pagerSorter: PagerSorter = PagerSorter.ForColumn(
        Coupons.Columns.GetDefaultSort(), 15, true
    );

    private actions: TableAction<Coupon>[] = [
        {
            Id: "stop",
            Name: $t( "COUPONS_actionStop" ),
            Multi: true,

            /* Only offer the action while every selected coupon is still live: stopping flags a
               coupon inactive, so an already-stopped one has nothing left to stop. */
            Enabled: ( coupons: Coupon[] ): boolean =>
                coupons.every( ( coupon: Coupon ): boolean => coupon.Active !== false )
        }
    ];

    private showForm: boolean = false;

    private async created() {
        /* Keep the list gated while columns load so MultiFilter mounts only after the default
           filter is assigned below. */
        this.id = undefined;

        const [ columns, error ] = await Coupons.Columns.Get();

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.columns = columns;

        this.setActiveFilter();
    }

    /* Pre-select the "Active" status so the list opens on redeemable coupons only. */
    private setActiveFilter(): void {
        const activeFilter = new CustomValue<string>( $t( "COUPONS_statusActive" ), "Active" );

        Filters.Assign( "Coupons", this.filters(), "Status", [ activeFilter ] );

        this.id = "Coupons";
    }

    private async update(
        filters: Filter[],
        pagerSorter: PagerSorter,
        resetPagination?: boolean
    ) {
        await this.$nextTick();
        this.$refs.Records.Loading();

        if( resetPagination )
            this.pagerSorter.Page = 1;

        const [ records, updatedPagerSorter, error ] = await Coupons.Find( filters, pagerSorter );

        this.records = records;
        this.pagerSorter = updatedPagerSorter;

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }
    }

    private toggleColumn( column: Column ) {
        Coupons.Columns.Toggle( column );
    }

    private executeAction( id: string, coupons: Coupon[] ) {
        const urls: string[] = [];
        let index: number = 0;

        switch( id ) {
            case "open":
                this.records.forEach( record =>
                    urls.push( "/coupons/" + record.Id )
                );

                index = this.records.findIndex( record => record.Id === coupons[ 0 ].Id );

                Records.Navigation.SetList( urls, index );

                this.$router.push( `/coupons/${coupons[ 0 ].Id}` );

                break;

            case "stop":
                EventBus.Publish( "Confirmation", {
                    Props: {
                        Message: this.$t( "COUPONS_stopConfirm" ),

                        Actions: [{
                            Id: "stop",
                            Name: this.$t( "COUPONS_actionStop" ),
                            Active: true
                        }]
                    },

                    Events: { Action: () => this.stop( coupons ) }
                });

                break;

            default:
                EventBus.Publish( "UnderConstruction" );
                break;
        }
    }

    private async stop( records: Coupon[] ) {
        for( const record of records ) {
            const error = await Coupons.Stop( record.Id );

            if( error !== undefined )
                EventBus.Publish(
                    "Error",
                    new CustomError( "Error", $t( "COUPONS_cannotStop" ) )
                );
        }

        this.refresh();
    }

    private refresh(): void {
        this.showForm = false;
        this.update( this.$refs.Records.AppliedFilters, this.pagerSorter, true );
    }
}
